import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Loader2,
  Mail,
  Edit,
  Trash2,
  Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { EmailTemplateModal } from '@/components/settings/email-template-modal';

export function EmailTemplatesPage() {
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<{
    id: string;
    name: string;
    subject: string;
    body: string;
    variables: string[];
    isActive: boolean;
  } | null>(null);

  const { data: templates, isLoading, error } = trpc.settings.listEmailTemplates.useQuery();

  const utils = trpc.useUtils();

  const seedMutation = trpc.settings.seedEmailTemplates.useMutation({
    onSuccess: () => {
      utils.settings.listEmailTemplates.invalidate();
    },
  });

  const deleteMutation = trpc.settings.deleteEmailTemplate.useMutation({
    onSuccess: () => {
      utils.settings.listEmailTemplates.invalidate();
    },
  });

  const handleEdit = (template: NonNullable<typeof templates>[number]) => {
    setEditingTemplate({
      id: template.id,
      name: template.name,
      subject: template.subject,
      body: template.body,
      variables: (template.variables as string[]) || [],
      isActive: template.isActive,
    });
    setShowTemplateModal(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleAddNew = () => {
    setEditingTemplate(null);
    setShowTemplateModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/settings">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Email Templates</h1>
          <p className="text-muted-foreground">Customize automated email notifications</p>
        </div>
        <div className="flex gap-2">
          {(!templates || templates.length === 0) && (
            <Button
              variant="outline"
              className="h-8"
              onClick={() => seedMutation.mutate()}
              disabled={seedMutation.isPending}
            >
              {seedMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-2 h-4 w-4" />
              )}
              Create Default Templates
            </Button>
          )}
          <Button className="h-8" onClick={handleAddNew}>
            <Plus className="mr-2 h-4 w-4" />
            Add Template
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading templates...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">Error loading templates</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Templates List */}
      {!isLoading && !error && templates && templates.length > 0 && (
        <div className="space-y-3">
          {templates.map((template) => (
            <div
              key={template.id}
              className={`border border-border bg-card p-4 ${
                !template.isActive ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2">
                    <Mail className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-medium">{template.name}</h3>
                      {!template.isActive && (
                        <span className="bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          Inactive
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Subject: {template.subject}
                    </p>
                    <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                      {template.body.substring(0, 150)}...
                    </p>
                    {template.variables && (template.variables as string[]).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(template.variables as string[]).map((variable) => (
                          <span
                            key={variable}
                            className="bg-muted px-1.5 py-0.5 text-xs font-mono"
                          >
                            {`{{${variable}}}`}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleEdit(template)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleDelete(template.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && (!templates || templates.length === 0) && (
        <div className="flex flex-col items-center justify-center border border-border bg-card py-12 text-center">
          <Mail className="h-10 w-10 text-muted-foreground/50" />
          <h3 className="mt-4 text-sm font-medium">No email templates</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Create templates for automated email notifications
          </p>
          <div className="mt-4 flex gap-2">
            <Button variant="outline" className="h-8" onClick={() => seedMutation.mutate()}>
              <Wand2 className="mr-2 h-4 w-4" />
              Create Default Templates
            </Button>
            <Button className="h-8" onClick={handleAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              Add Template
            </Button>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <EmailTemplateModal
          open={showTemplateModal}
          onOpenChange={setShowTemplateModal}
          template={editingTemplate}
          onSuccess={() => {
            utils.settings.listEmailTemplates.invalidate();
          }}
        />
      )}
    </div>
  );
}
